import { useMutation, useQuery } from '@tanstack/react-query';
import { selfOnboardApi } from '../api/selfOnboardApi';
import type { SelfOnboardPayload, SignContractPayload } from '../types';

const KEY = 'self-onboard-contract';

/**
 * Load the contract assigned to a self-onboarding applicant for review.
 * Disabled until both the merchantId and single-use token (from the emailed link) are present.
 */
export const useSelfOnboardContract = (merchantId: string | null, token: string | null) =>
  useQuery({
    queryKey: [KEY, merchantId, token],
    queryFn: () => selfOnboardApi.getContractForSigning(merchantId as string, token as string),
    enabled: !!merchantId && !!token,
    // Single-use token + inert content — no benefit to background refetching.
    staleTime: Infinity,
    retry: false,
  });

/** Submit the signing/acceptance event. */
export const useSignSelfOnboardContract = (merchantId: string | null) =>
  useMutation({
    mutationFn: (payload: SignContractPayload) =>
      selfOnboardApi.signContract(merchantId as string, payload),
  });

/**
 * Industries + business categories for the public sign-up form.
 *
 * Unauthenticated and effectively static within a session, so it is cached hard;
 * `retry: false` because a failure here means the form cannot be completed at
 * all and the page says so, rather than spinning.
 */
export const useOnboardingTaxonomy = () =>
  useQuery({
    queryKey: ['self-onboard-taxonomy'],
    queryFn: () => selfOnboardApi.getTaxonomy(),
    staleTime: Infinity,
    retry: false,
  });

/** Submit the sign-up application. Success returns a merchantId; no session is created. */
export const useSubmitSelfOnboard = () =>
  useMutation({
    mutationFn: (payload: SelfOnboardPayload) => selfOnboardApi.submit(payload),
  });
